import * as _ from "lodash";
import {equals, ILogService} from "angular";
import {Application} from "../../model/Application";
import {Assessment} from "../../model/Assessment";
import {User} from "../../model/User";
import {ApplicationService} from "../../application/application.service";
import {IModalService} from "angular-ui-bootstrap";
import {CreditSummary} from "../../model/CreditSummary";

class AssessmentsController {

    private currentUser: User;
    private application: Application;
    private assessments: Assessment[];
    private approvedAssessmentsCreditSummary: CreditSummary[];
    private actionsEnabled: boolean;
    private showErrors: boolean;
    private originalAssessment: Assessment;

    private selectedAssessmentIds: string[] = [];
    private activeAssessmentIndex: number = null;

    private notifyAssessmentsChanged: () => any;

    private isNewAssessment: boolean;

    /* @ngInject */
    constructor(private $log: ILogService,
                private applicationService: ApplicationService,
                private $uibModal: IModalService,
                private $state: any,
                private toaster: any) {
    }

    public $onInit() {
        this.$log.debug("AssessmentsController, currentUser: %o", this.currentUser);
    }

    public addNewAssessment() {
        if (this.assessments == null) {
            this.assessments = [];
        }

        this.assessments.push(new Assessment());
        this.notifyAssessmentsChanged();
        this.activeAssessmentIndex = this.assessments.length - 1;
        this.isNewAssessment = true;
    }

    public showAssessment(index) {
        this.activeAssessmentIndex = index;
        this.originalAssessment = _.cloneDeep(this.assessments[index]);
        this.isNewAssessment = false;
    }

    public showAssessmentList() {
        this.$log.debug("Assessment modification check: original %o, current %o", this.originalAssessment, this.assessments[this.activeAssessmentIndex]);
        if (equals(this.originalAssessment, this.assessments[this.activeAssessmentIndex])) {
            this.activeAssessmentIndex = null;
        } else {
            this.$uibModal.open({
                component: "navigatingAwayModal"
            }).result.then(() => {
                if (this.isNewAssessment) {
                    this.assessments.pop();
                } else {
                    this.assessments[this.activeAssessmentIndex] = _.cloneDeep(this.originalAssessment);
                }
                this.isNewAssessment = false;
                this.notifyAssessmentsChanged();
                this.activeAssessmentIndex = null;
            }, (reason) => {
                this.$log.info("modal dismissed: " + reason);
            });
        }
    }

    public saveAssessment(application: Application, assessment: Assessment, index: number) {
        this.$log.debug("saveAssessment:", application, assessment, index);
        this.applicationService.saveAssessment(application, assessment)
            .then((newAssessment) => {
                this.$log.debug("saveAssessment response:", newAssessment);
                this.assessments[index] = newAssessment;
                this.notifyAssessmentsChanged();
                this.originalAssessment = _.extend(newAssessment);
                this.isNewAssessment = false;
                this.showErrors = false;
                this.toaster.pop({type: "success", title: "Assessment saved successfully"});
            });
    }

    public removeAssessment(application: Application, assessment: Assessment, index: number) {
        this.$log.debug("removeAssessment:", application, assessment, index);

        if (!assessment.id) {
            this.assessments.splice(index, 1);
            this.activeAssessmentIndex = null;
            return;
        }

        this.applicationService.removeAssessment(application, assessment)
            .then(() => {
                this.$log.debug("removeAssessment response");

                this.assessments.splice(index, 1);
                this.notifyAssessmentsChanged();
                this.activeAssessmentIndex = null;

                this.toaster.pop({type: "success", title: "Assessment removed successfully"});
                this.resetSummary(assessment);
            });
    }

    public shareAssessments(application: Application, assessment: Assessment, index: number) {
        this.$log.debug("shareAssessment", application, assessment);

        this.$uibModal.open({
            component: "assessmentSharingModal",
            resolve: {
                application: () => application,
                assessmentIds: () => this.selectedAssessmentIds
            }
        }).result.then((newAssessments) => {
            this.$log.info("Invite sent: ", newAssessments);
            this.$state.reload();
        }, (reason) => {
            this.$log.info("modal dismissed: " + reason);
        });
    }

    public shareAssessment(application: Application, assessment: Assessment, index: number) {
        this.$log.debug("shareAssessment", application, assessment);

        this.$uibModal.open({
            component: "assessmentSharingModal",
            resolve: {
                application: () => application,
                assessment: () => assessment
            }
        }).result.then((newAssessment) => {
            this.$log.info("Invite sent: ", newAssessment);
            this.assessments[index] = newAssessment;
            this.notifyAssessmentsChanged();
            this.originalAssessment = _.extend(newAssessment);
        }, (reason) => {
            this.$log.info("modal dismissed: " + reason);
        });
    }

    public isSaveEnabled(assessment: Assessment): boolean {
        return this.actionsEnabled ||
            (this.isAssessorForThisAssesment(assessment) &&
                assessment.status !== "APPROVED" &&
                assessment.status !== "REJECTED");
    }

    public isRemoveEnabled(assessment: Assessment): boolean {
        return this.actionsEnabled;
    }

    public isResetEnabled(assessment: Assessment): boolean {
        return this.actionsEnabled && assessment.status === "APPROVED" || assessment.status === "REJECTED";
    }

    public isRejectEnabled(assessment: Assessment): boolean {
        return this.actionsEnabled && assessment.status === "IN_PROGRESS";
    }

    public isApproveEnabled(assessment: Assessment): boolean {
        return this.actionsEnabled && assessment.status === "IN_PROGRESS" && assessment.precedentStatus !== "REJECTED";
    }

    public isInviteEnabled(assessment: Assessment): boolean {
        return this.actionsEnabled && assessment.status === "IN_PROGRESS";
    }

    public isAssessorForThisAssesment(assessment: Assessment): boolean {
        if (!this.currentUser) {
            return false;
        } else {
            return assessment && _.includes(assessment.collaborators, this.currentUser.email);
        }
    }

    public approveAssessment(application: Application, assessment: Assessment, index: number) {
        this.$log.debug("approveAssessment:", application, assessment, index);
        if (!this.validateAssessment(assessment)) {
            this.showErrors = true;
            this.toaster.pop({type: "failure", title: "Cannot approve an assessment, please provide missing information"});
        } else {
            this.applicationService.saveAssessment(application, assessment).then((newAssessment) => {
                this.applicationService.approveAssessment(application, newAssessment)
                    .then((approvedAssessment) => {
                        this.$log.debug("approveAssessment response:", approvedAssessment);

                        this.assessments[index] = approvedAssessment;
                        this.notifyAssessmentsChanged();
                        this.originalAssessment = _.extend(approvedAssessment);

                        this.toaster.pop({type: "success", title: "Assessment accepted"});
                    });
            });
        }
    }

    public rejectAssessment(application: Application, assessment: Assessment, index: number) {
        this.$log.debug("rejectAssessment:", application, assessment, index);

        this.applicationService.saveAssessment(application, assessment)
            .then((newAssessment) => {
                return this.applicationService.rejectAssessment(application, newAssessment);
            })
            .then((rejectedAssessment) => {
                this.$log.debug("rejectAssessment response:", rejectedAssessment);

                this.assessments[index] = rejectedAssessment;
                this.notifyAssessmentsChanged();
                this.originalAssessment = _.extend(rejectedAssessment);

                this.toaster.pop({type: "success", title: "Assessment rejected"});
            });
    }

    public canCreatePrecedent(assessment: Assessment): boolean {
        return !assessment.precedentId;
    }

    public createPrecedent(application: Application, assessment: Assessment) {
        this.$log.debug("createPrecedent:", assessment);
        this.$uibModal.open({
            component: "createPrecedentModal",
            resolve: {
                application: () => application,
                assessment: () => assessment
            },
            size: "lg"
        }).result.then((precedent) => {
            this.$log.info("Precedent created: ", precedent);
        }, (reason) => {
            this.$log.info("modal dismissed: " + reason);
        });
    }

    public resetAssessment(application: Application, assessment: Assessment, index: number) {
        this.$log.debug("resetAssessment:", application, assessment, index);

        this.applicationService.resetAssessment(application, assessment)
            .then((newAssessment) => {
                this.$log.debug("resetAssessment response:", newAssessment);

                this.assessments[index] = newAssessment;
                this.notifyAssessmentsChanged();

                this.toaster.pop({type: "success", title: "Assessment reset"});

                // re-calculate the approved assessments summary
                this.resetSummary(newAssessment);
            });
    }

    /***
     * Re calculate the credit points summary once the assessment state changes from APPROVED
     * @param assessment
     * @private
     */
    private resetSummary(assessment: Assessment) {
        const credits = assessment.credits;
        const courseCodes = assessment.courseCodes;
        courseCodes.forEach((code) => {
            code = code.substring(0, code.length - 1);
            this.approvedAssessmentsCreditSummary.filter((s) => s.courseCode == code).forEach((s) => {
                credits.forEach(c => {
                    if (c.type == 'SPECIFIED_CREDIT') {
                        s.specifiedCreditsInApplication -= c.creditPoints;
                    } else if (c.type == 'UNSPECIFIED_CREDIT') {
                        s.unspecifiedCreditsInApplication -= c.creditPoints;
                    }
                });
            });
        });

    }

    private validateAssessment(assessment: Assessment): boolean {
        this.$log.log("Validating assessment %o", assessment);

        let valid = (
            assessment.courseCodes &&
            assessment.courseCodes.length > 0 &&
            assessment.priorCourseUnits &&
            assessment.priorCourseUnits.length > 0
        );

        if (!assessment.credits || assessment.credits.length === 0) {
            this.$log.log("No credits specified");
            return false;
        }

        assessment.credits.forEach((credit) => {

            // Exclude this condition, as credit does has 0 points, e.g. OHS10000
            // if(credit.specifiedCreditType !== "PRECLUSION") {
            //     valid = valid && credit.creditPoints > 0;
            // }

            if (credit.type === "SPECIFIED_CREDIT") {
                valid = valid && (credit.monashUnitCode && credit.monashUnitCode.length > 0 &&
                    credit.monashUnitVersion && credit.monashUnitVersion.length > 0 &&
                    credit.specifiedCreditType);
            } else if (credit.type === "UNSPECIFIED_CREDIT") {
                valid = valid && (credit.unitLevel && credit.unitLevel.length > 0);
            }
        });
        this.$log.log("Validation result: %o", valid);
        return valid;
    }

}

export const AssessmentsComponent: angular.IComponentOptions = {
    bindings: {
        application: "=",
        assessments: "=",
        actionsEnabled: "<?",
        currentUser: "<",
        notifyAssessmentsChanged: "&",
        approvedAssessmentsCreditSummary: "="
    },
    controller: AssessmentsController,
    template: require("./assessments.html")
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/main/client/app/shared/assessment/assessments/assessments.component.ts